import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminStatistics } from '../hooks/useAdminStatistics';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Users, FileText, Camera, Video, Crown, Star, Gem, Zap } from 'lucide-react';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { data: stats, isLoading, error } = useAdminStatistics();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16 mb-2" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="mb-6">
        <CardContent className="flex items-center justify-center py-6">
          <p className="text-destructive">{t('adminPanel.dashboard.errorLoading')}</p>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'free': return <Users className="h-4 w-4" />;
      case 'standard': return <Star className="h-4 w-4" />;
      case 'premium': return <Crown className="h-4 w-4" />;
      case 'ultimate': return <Zap className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'free': return 'bg-slate-500';
      case 'standard': return 'bg-blue-500';
      case 'premium': return 'bg-purple-500';
      case 'ultimate': return 'bg-gradient-to-r from-yellow-400 to-orange-500';
      default: return 'bg-slate-500';
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {/* Total Users */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('adminPanel.dashboard.totalUsers')}</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalUsers.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {t('adminPanel.dashboard.registeredUsers')}
          </p>
        </CardContent>
      </Card>

      {/* Users by Tier */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('adminPanel.dashboard.usersByPlan')}</CardTitle>
          <Crown className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(stats.usersByTier).map(([tier, count]) => (
              <div key={tier} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getTierColor(tier)}`} />
                  <span className="text-sm capitalize">{t(`adminPanel.subscriptionTiers.${tier}`)}</span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {count}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Total Posts */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('adminPanel.dashboard.totalPosts')}</CardTitle>
          <FileText className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalPosts.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {t('adminPanel.dashboard.postsCreated')}
          </p>
        </CardContent>
      </Card>

      {/* Total Photos */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('adminPanel.dashboard.totalPhotos')}</CardTitle>
          <Camera className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalPhotos.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {t('adminPanel.dashboard.imagesUploaded')}
          </p>
        </CardContent>
      </Card>

      {/* Total Videos */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('adminPanel.dashboard.totalVideos')}</CardTitle>
          <Video className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalVideos.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {t('adminPanel.dashboard.videosUploaded')}
          </p>
        </CardContent>
      </Card>

      {/* Content Summary */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('adminPanel.dashboard.contentSummary')}</CardTitle>
          <Gem className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm">{t('adminPanel.dashboard.posts')}</span>
              <Badge variant="outline">{stats.totalPosts}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">{t('adminPanel.dashboard.photos')}</span>
              <Badge variant="outline">{stats.totalPhotos}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">{t('adminPanel.dashboard.videos')}</span>
              <Badge variant="outline">{stats.totalVideos}</Badge>
            </div>
            <div className="flex justify-between pt-2 border-t">
              <span className="text-sm font-medium">{t('adminPanel.dashboard.totalMedia')}</span>
              <Badge>{stats.totalPhotos + stats.totalVideos}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
